import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/common/Input';
import { validateEmail, validateRequired } from '../../utils/validators';
import { unwrapDrf } from '../../utils/helpers';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isEmployee, error: authError, clearError, isLoggingIn } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);

  // Read ?next= param for post-login redirect (e.g., from QR code scan)
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next');

  useEffect(() => {
    if (isAuthenticated) {
      if (nextUrl && nextUrl.startsWith('/')) {
        navigate(nextUrl, { replace: true });
      } else {
        navigate(isEmployee ? '/employee/dashboard' : '/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, isEmployee, navigate, nextUrl]);

  useEffect(() => {
    return () => { if (clearError) clearError(); };
  }, [clearError]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
    if (authError && clearError) clearError();
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.email)) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!validateRequired(formData.password)) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try { await login(formData.email, formData.password); }
    catch (err) {
      const approvalStatus = unwrapDrf(err?.data?.approval_status);
      if (approvalStatus === 'pending') {
        if (clearError) clearError();
        navigate('/signup?pending=true', { replace: true });
        return;
      }
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="login-page">

      {/* ── Left hero panel ──────────────────────────────────── */}
      <div className="login-page__hero">
        <div className="login-page__hero-video-wrapper">
          <iframe
            src="https://www.youtube.com/embed/UB43h0p63YM?autoplay=1&mute=1&loop=1&playlist=UB43h0p63YM&controls=0&rel=0&playsinline=1&showinfo=0&modestbranding=1"
            title="Deep Roots Milling background"
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
          />
        </div>

        <div className="login-page__hero-content">
          <div className="login-page__brand">
            <p className="login-page__wordmark">Deep Roots Milling</p>
            <div className="login-page__divider">
              <span className="login-page__divider-line" />
              <span className="login-page__divider-ornament">❧</span>
              <span className="login-page__divider-line" />
            </div>
          </div>

          <h1 className="login-page__hero-title">
            Stone-Milled Grain,<br />Direct from Our Mill
          </h1>

          <ol className="login-page__features">
            <li className="login-page__feature">
              <span className="login-page__feature-number">1</span>
              <div className="login-page__feature-text">
                <strong>Browse Our Full Catalog</strong>
                <p>Freshly milled flours, whole grains, and specialty products direct from our stone mill.</p>
              </div>
            </li>
            <li className="login-page__feature">
              <span className="login-page__feature-number">2</span>
              <div className="login-page__feature-text">
                <strong>Place & Track Orders</strong>
                <p>Streamlined wholesale ordering with real-time status updates from mill to your door.</p>
              </div>
            </li>
            <li className="login-page__feature">
              <span className="login-page__feature-number">3</span>
              <div className="login-page__feature-text">
                <strong>Manage Your Account</strong>
                <p>View order history, update your details, and stay connected with your mill.</p>
              </div>
            </li>
          </ol>
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────────── */}
      <div className="login-page__form-panel">
        <div className="login-page__form-container">

          <div className="login-page__header">
            <h2 className="login-page__title">Welcome Back</h2>
            <p className="login-page__subtitle">Wholesale portal — sign in to continue</p>
          </div>

          {authError && (() => {
            const approvalStatus = unwrapDrf(authError.data?.approval_status);
            if (approvalStatus === 'pending') return null;
            const reason = unwrapDrf(authError.data?.reason);
            return (
              <div className="login-page__error" role="alert">
                {approvalStatus === 'rejected' ? (
                  <>
                    <strong>Account Not Approved</strong>
                    <p className="login-page__error-detail">
                      Your account application was not approved.
                      {reason && <> Reason: {reason}</>}
                    </p>
                  </>
                ) : (
                  authError.message || 'Login failed. Please check your credentials and try again.'
                )}
              </div>
            );
          })()}

          <form className="login-page__form" onSubmit={handleSubmit} noValidate>
            <Input
              type="email"
              name="email"
              label="Email Address"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              placeholder="you@company.com"
              autoComplete="email"
              required
              autoFocus
            />

            <Input
              type="password"
              name="password"
              label="Password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              error={errors.password}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
            />

            <div className="login-page__options">
              <label className="login-page__checkbox">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="login-page__forgot-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="login-page__submit"
              disabled={isLoggingIn}
            >
              {isLoggingIn ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>

          <div className="login-page__footer">
            <p>
              Don't have an account?{' '}
              <Link to="/signup" className="login-page__link">Request access</Link>
            </p>
            <p>
              <Link to="/catalog" className="login-page__link">Browse our wholesale catalog →</Link>
            </p>
          </div>

        </div>
      </div>

    </div>
  );
};

export default Login;
